import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageHeader, Kicker } from "@/components/Section";
import { CTA } from "@/components/CTA";
import { ClassifiedDossier } from "@/components/transmission/ClassifiedDossier";

export default function SchoolPartners() {
  const points = [
    { t: "Project-based collaboration", b: "Students work on real creative briefs alongside a multidisciplinary crew." },
    { t: "Portfolio development", b: "Every project ends with real, showcase-ready work students fully own." },
    { t: "Experiential learning", b: "Hands-on industry experience that classrooms alone can't replicate." },
    { t: "School-approved credit", b: "Academic or lab credit may be coordinated — subject to your school's approval." },
  ];
  return (
    <div className="bg-onyx text-cream">
      <PageHeader
        kicker="For Schools"
        titleLines={["Real-world", "experience", "for students."]}
        subtitle="Partner with Big Little Village to connect students with multidisciplinary creative projects designed for portfolio development and hands-on experience."
      />

      <section className="grain-dark border-b border-gold/20 bg-onyx py-20 sm:py-28">
        <div className="relative mx-auto grid max-w-[1400px] gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <Reveal><Kicker>How It Works</Kicker></Reveal>
            <div className="mt-8 space-y-4">
              {points.map((p, i) => (
                <Reveal key={p.t} delay={i * 0.08}>
                  <div className="flex gap-4 border border-silver/20 bg-charcoal/70 p-5 transition-colors hover:border-gold/40">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-gold bg-gradient-to-b from-goldlight to-gold text-onyx">
                      <Check size={20} />
                    </div>
                    <div>
                      <h3 className="font-head text-lg font-extrabold uppercase tracking-tight text-cream">{p.t}</h3>
                      <p className="mt-1 font-body text-sm leading-relaxed text-silver/75">{p.b}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}>
              <div className="mt-6 flex items-start gap-3 border border-gold/40 bg-charcoal p-5">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                <p className="font-body font-semibold text-goldlight">
                  Any lab or academic credit is described as subject to approval by the participating school.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div id="inquiry" className="gold-frame grain-dark relative bg-charcoal p-8 sm:p-10">
              <span className="absolute -top-4 right-6 -rotate-3 border border-gold/50 bg-onyx px-2.5 py-0.5 font-marker text-lg text-goldlight">for the students ✦</span>
              <Kicker>Partnership Inquiry</Kicker>
              <h2 className="mt-5 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic">Let's build something for your students.</h2>
              <p className="mt-4 font-body leading-relaxed text-silver/80">
                Our full partnership inquiry covers your programs, credit and hour requirements, logistics, and
                the collaboration formats that fit your institution. It takes about five minutes.
              </p>
              <CTA to="/apply/school" variant="pink" className="mt-8" data-testid="school-inquiry-cta">
                Start the Inquiry <ArrowRight size={18} />
              </CTA>
              <p className="mt-4 font-body text-xs text-silver/50">
                Academic, lab, practicum or internship credit is always determined by the educational institution.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="grain-dark border-t border-gold/20 bg-charcoal py-16 text-center sm:py-20">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Reveal><Kicker>Intercepted for you</Kicker></Reveal>
          <div className="mt-8"><ClassifiedDossier kind="education" /></div>
        </div>
      </section>
    </div>
  );
}
